import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";
import api, { BACKEND } from "@/lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] },
  }),
};

/**
 * Reusable Testimonials section — pulls from /api/cms/testimonials.
 * Used on Home and About (single source of truth).
 */
export default function TestimonialsSection({
  eyebrow = "Voices from the field",
  title = "Principals and teachers love it.",
  description,
  limit,
  tone = "muted",
  testid = "testimonials-section",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .get("/cms/testimonials")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && items.length === 0) return null;
  const shown = limit ? items.slice(0, limit) : items;

  const bg =
    tone === "dark"
      ? "bg-[#050914] text-white"
      : tone === "white"
        ? "bg-white"
        : "bg-[#F9FAFB] border-y border-gray-100";

  return (
    <section className={bg} data-testid={testid}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="max-w-2xl mb-14">
          <div
            className={`text-xs uppercase tracking-[0.2em] font-semibold mb-3 ${
              tone === "dark" ? "text-emerald-300" : "text-[#4F46E5]"
            }`}
          >
            {eyebrow}
          </div>
          <h2
            className={`font-display text-4xl sm:text-5xl tracking-tight font-semibold ${
              tone === "dark" ? "text-white" : "text-[#0F172A]"
            }`}
          >
            {title}
          </h2>
          {description && (
            <p className={`mt-4 text-lg ${tone === "dark" ? "text-white/70" : "text-gray-600"}`}>
              {description}
            </p>
          )}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {shown.map((t, i) => (
            <motion.figure
              key={t.id}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-40px" }}
              custom={i}
              className={`rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col ${
                tone === "dark"
                  ? "bg-white/5 border border-white/10 backdrop-blur"
                  : "bg-white border border-gray-200"
              }`}
              data-testid={`testimonial-${t.id}`}
            >
              <Quote
                className={`w-6 h-6 mb-3 ${
                  tone === "dark" ? "text-indigo-300/40" : "text-[#4F46E5]/30"
                }`}
              />
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: t.rating || 5 }).map((_, si) => (
                  <Star key={si} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <blockquote
                className={`text-[15px] leading-relaxed flex-1 ${
                  tone === "dark" ? "text-white/80" : "text-gray-700"
                }`}
              >
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption
                className={`mt-5 flex items-center gap-3 pt-4 border-t ${
                  tone === "dark" ? "border-white/10" : "border-gray-100"
                }`}
              >
                {t.photo ? (
                  <img
                    src={t.photo.startsWith("http") ? t.photo : `${BACKEND}${t.photo}`}
                    alt={t.author}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-emerald-400 grid place-items-center text-white font-semibold text-sm">
                    {(t.author || "?").charAt(0)}
                  </div>
                )}
                <div>
                  <div
                    className={`text-sm font-semibold ${
                      tone === "dark" ? "text-white" : "text-[#0F172A]"
                    }`}
                  >
                    {t.author}
                  </div>
                  <div className={`text-xs ${tone === "dark" ? "text-white/50" : "text-gray-500"}`}>
                    {[t.role, t.school].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
